import {
  type Decimal,
  fromScaled,
  parseDecimal,
  trimDecimal,
} from "@/lib/tool-kit/arithmetic/decimal";
import { BIG_ONE, BIG_ZERO, pow10Big } from "@/lib/tool-kit/math/bigint";
import { toSuperscript } from "@/lib/tool-kit/math/primes";
import {
  addR,
  makeRational,
  mulR,
  parseRational,
  RATIONAL_ZERO,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/**
 * Place value: the same number as a sum of digit × place, in words, and as a chart.
 *
 * Everything works on the digit string, so a 30-digit number loses nothing. Zeros in the
 * middle of a number are skipped in the written-out sums by default, as teachers do
 * (4,032 = 4000 + 30 + 2), and can be shown as 0 × 100 on request.
 */
export const MAX_INTEGER_DIGITS = 36;
export const MAX_FRACTION_DIGITS = 30;

export interface PlaceDigit {
  digit: number;
  /** Power of ten of the place: 3 = thousands, 0 = ones, −1 = tenths. */
  exponent: number;
}

/** Digits from the most significant to the last, with the power of ten of each place. */
export function placeDigits(value: Decimal): PlaceDigit[] {
  const places: PlaceDigit[] = [];
  const intLength = value.int.length;
  for (let i = 0; i < intLength; i++) {
    places.push({ digit: Number(value.int[i]), exponent: intLength - 1 - i });
  }
  for (let i = 0; i < value.frac.length; i++) {
    places.push({ digit: Number(value.frac[i]), exponent: -(i + 1) });
  }
  return places;
}

/** The value of one place, digit × 10^exponent, as plain decimal text. */
export function termValue(place: PlaceDigit, mark = "."): string {
  const { digit, exponent } = place;
  if (digit === 0) {
    return "0";
  }
  if (exponent >= 0) {
    return `${digit}${"0".repeat(exponent)}`;
  }
  return `0${mark}${"0".repeat(-exponent - 1)}${digit}`;
}

export type ExpandedStyle = "sum" | "products" | "powers" | "fractions";

function kept(
  places: readonly PlaceDigit[],
  includeZeros: boolean
): PlaceDigit[] {
  const nonZero = places.filter((p) => p.digit !== 0);
  if (includeZeros) {
    return places.length > 0 ? [...places] : [];
  }
  if (nonZero.length > 0) {
    return nonZero;
  }
  return places.filter((p) => p.exponent === 0);
}

function powerText(place: PlaceDigit): string {
  return `10${toSuperscript(place.exponent)}`;
}

function fractionText(place: PlaceDigit): string {
  return place.exponent >= 0
    ? `1${"0".repeat(place.exponent)}`
    : `1/1${"0".repeat(-place.exponent)}`;
}

/** One written-out form of the number. */
export function expandedText(
  value: Decimal,
  style: ExpandedStyle,
  includeZeros: boolean,
  mark = "."
): string {
  const terms = kept(placeDigits(value), includeZeros);
  const parts = terms.map((place) => {
    if (style === "sum") {
      return termValue(place, mark);
    }
    if (style === "products") {
      const unit =
        place.exponent >= 0
          ? `1${"0".repeat(place.exponent)}`
          : `0${mark}${"0".repeat(-place.exponent - 1)}1`;
      return `(${place.digit} × ${unit})`;
    }
    if (style === "powers") {
      return `(${place.digit} × ${powerText(place)})`;
    }
    return `(${place.digit} × ${fractionText(place)})`;
  });
  return parts.join(" + ");
}

/* ----------------------------------------------------------------- names */

export interface PlaceWords {
  ones: string;
  tens: string;
  hundreds: string;
  /** "thousands", "millions" … index 1 upward. */
  periodPlurals: readonly string[];
  tenPrefix: string;
  hundredPrefix: string;
  tenth: string;
  hundredth: string;
  /** "thousandth", "millionth" … index 0 = thousandth. */
  fractionBases: readonly string[];
  fractionTenPrefix: string;
  fractionHundredPrefix: string;
  /** Plural of a fraction place, singular at `{unit}`. English "{unit}s". */
  fractionPlural: string;
  /**
   * Ending removed from the singular before `fractionPlural` is applied, when the
   * plural replaces it. English "", Italian "o" ("decimo" → "decim" + "i" = "decimi").
   */
  fractionDropEnding: string;
  /**
   * Article (with its own trailing space or apostrophe) placed before a place name in
   * a sentence, so each language can agree it with the name's gender and number.
   * Order: ones, tens, hundreds, "ten …" prefixed, "hundred …" prefixed.
   */
  articleWhole: readonly string[];
  /** Article for a bare period name ("thousands", "millions" …), by period index. */
  articlePeriods: readonly string[];
  /** Article for every fraction place ("tenths", "ten-thousandths" …). */
  articleFraction: string;
}

const UNIT_PLACEHOLDER = "{unit}";

/** "thousands", "ten thousands", "hundred thousands", "tenths", "ten-thousandths" … */
export function placeName(
  exponent: number,
  words: PlaceWords,
  plural = true
): string {
  if (exponent >= 0) {
    const group = Math.floor(exponent / 3);
    const rest = exponent % 3;
    if (group === 0) {
      return [words.ones, words.tens, words.hundreds][rest];
    }
    const base = words.periodPlurals[group] ?? "";
    const prefix = ["", words.tenPrefix, words.hundredPrefix][rest];
    return `${prefix}${base}`;
  }
  const s = -exponent;
  let unit = "";
  if (s === 1) {
    unit = words.tenth;
  } else if (s === 2) {
    unit = words.hundredth;
  } else {
    const rest = s % 3;
    const base = words.fractionBases[Math.floor((s - rest) / 3) - 1] ?? "";
    const prefix = ["", words.fractionTenPrefix, words.fractionHundredPrefix][
      rest
    ];
    unit = `${prefix}${base}`;
  }
  if (!plural) {
    return unit;
  }
  const drop = words.fractionDropEnding;
  const stem =
    drop !== "" && unit.endsWith(drop) ? unit.slice(0, -drop.length) : unit;
  return words.fractionPlural.replace(UNIT_PLACEHOLDER, stem);
}

/** The article that goes before `placeName(exponent, …)` in a sentence ("the "). */
export function placeArticle(exponent: number, words: PlaceWords): string {
  if (exponent < 0) {
    return words.articleFraction;
  }
  const group = Math.floor(exponent / 3);
  const rest = exponent % 3;
  if (group === 0) {
    return words.articleWhole[rest] ?? "";
  }
  if (rest === 0) {
    return words.articlePeriods[group] ?? "";
  }
  return words.articleWhole[rest + 2] ?? "";
}

/** A place name ready for a sentence: its article, then the plural name. */
export function placeInSentence(exponent: number, words: PlaceWords): string {
  return `${placeArticle(exponent, words)}${placeName(exponent, words)}`;
}

/* ----------------------------------------------------------------- words */

export interface NumberWords {
  /** zero … nineteen. */
  ones: readonly string[];
  /** Index 2 = twenty … 9 = ninety. */
  tens: readonly string[];
  hundred: string;
  /** "", "thousand", "million" … */
  scales: readonly string[];
  and: string;
  point: string;
  negative: string;
  zero: string;
}

export interface WordOptions {
  /** British style: "one hundred and five". */
  useAnd: boolean;
  /** "fractional": "… and fifty-seven hundredths"; "point": "… point five seven". */
  decimals: "fractional" | "point";
}

function belowHundred(n: number, words: NumberWords): string {
  if (n < 20) {
    return words.ones[n];
  }
  const unit = n % 10;
  const tens = words.tens[Math.floor(n / 10)];
  return unit === 0 ? tens : `${tens}-${words.ones[unit]}`;
}

function belowThousand(n: number, words: NumberWords, useAnd: boolean): string {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds === 0) {
    return belowHundred(rest, words);
  }
  const head = `${words.ones[hundreds]} ${words.hundred}`;
  if (rest === 0) {
    return head;
  }
  return `${head}${useAnd ? ` ${words.and}` : ""} ${belowHundred(rest, words)}`;
}

/** A whole number (digit string) in words. */
export function wholeNumberWords(
  digits: string,
  words: NumberWords,
  useAnd: boolean
): string {
  const trimmed = digits.replace(LEADING_ZEROS, "") || "0";
  if (trimmed === "0") {
    return words.zero;
  }
  const groups: number[] = [];
  for (let end = trimmed.length; end > 0; end -= 3) {
    groups.unshift(Number(trimmed.slice(Math.max(0, end - 3), end)));
  }
  const parts: string[] = [];
  for (let index = 0; index < groups.length; index++) {
    const group = groups[index];
    if (group === 0) {
      continue;
    }
    const scale = words.scales[groups.length - 1 - index];
    const isLast = index === groups.length - 1;
    let text = belowThousand(group, words, useAnd);
    if (useAnd && isLast && group < 100 && parts.length > 0) {
      text = `${words.and} ${text}`;
    }
    parts.push(scale ? `${text} ${scale}` : text);
  }
  return parts.join(" ");
}

const LEADING_ZEROS = /^0+/;

export interface FractionNames {
  singular: (exponent: number) => string;
  plural: (exponent: number) => string;
}

/** The number in words, decimals included. */
export function numberInWords(
  value: Decimal,
  words: NumberWords,
  options: WordOptions,
  names: FractionNames
): string {
  const trimmed = trimDecimal(value);
  const whole = wholeNumberWords(trimmed.int, words, options.useAnd);
  const sign = trimmed.negative ? `${words.negative} ` : "";
  if (trimmed.frac === "") {
    return `${sign}${whole}`;
  }
  if (options.decimals === "point") {
    const digitWords = [...trimmed.frac].map((d) => words.ones[Number(d)]);
    return `${sign}${whole} ${words.point} ${digitWords.join(" ")}`;
  }
  const exponent = -trimmed.frac.length;
  const fraction = wholeNumberWords(trimmed.frac, words, false);
  const unit =
    BigInt(trimmed.frac) === BIG_ONE
      ? names.singular(exponent)
      : names.plural(exponent);
  if (trimmed.int === "0") {
    return `${sign}${fraction} ${unit}`;
  }
  return `${sign}${whole} ${words.and} ${fraction} ${unit}`;
}

/* ------------------------------------------------------------ scientific */

/** The number as m × 10^e with 1 ≤ m < 10 — what UK courses call "standard form". */
export function scientificParts(
  value: Decimal
): { mantissa: string; exponent: number } | null {
  const all = (value.int + value.frac).replace(LEADING_ZEROS, "");
  if (all === "") {
    return null;
  }
  const lead = (value.int + value.frac).length - all.length;
  const exponent = value.int.length - 1 - lead;
  const rest = all.slice(1).replace(TRAILING_ZEROS, "");
  return { mantissa: rest === "" ? all[0] : `${all[0]}.${rest}`, exponent };
}

const TRAILING_ZEROS = /0+$/;

/* ----------------------------------------------------------- sum parsing */

const TIMES = /\s*[x×*·]\s*/;
const POWER_OF_TEN = /^10\s*\^\s*\(?\s*([+\-−]?\d+)\s*\)?$/;
const UNICODE_MINUS = /−/g;

function termToRational(term: string): Rational | null {
  const factors = term.trim().split(TIMES);
  let product: Rational = makeRational(BIG_ONE);
  for (const factor of factors) {
    const text = factor.trim().replace(UNICODE_MINUS, "-");
    const power = POWER_OF_TEN.exec(text);
    if (power) {
      const k = Number.parseInt(power[1].replace(UNICODE_MINUS, "-"), 10);
      if (Math.abs(k) > MAX_FRACTION_DIGITS + MAX_INTEGER_DIGITS) {
        return null;
      }
      const ten = pow10Big(Math.abs(k));
      product = mulR(
        product,
        k >= 0 ? makeRational(ten) : makeRational(BIG_ONE, ten)
      );
      continue;
    }
    const parsed = parseRational(text);
    if (parsed === null) {
      return null;
    }
    product = mulR(product, parsed);
  }
  return product;
}

/** A rational whose denominator divides a power of ten, as an exact decimal. */
export function rationalToDecimal(value: Rational): Decimal | null {
  for (let k = 0; k <= MAX_FRACTION_DIGITS; k++) {
    const scale = pow10Big(k);
    if (scale % value.den === BIG_ZERO) {
      return fromScaled((value.num * scale) / value.den, k);
    }
  }
  return null;
}

export interface ExpandedInput {
  value: Decimal;
  /** True when the reader typed a sum of terms, like 4000 + 500 + 30 + 2. */
  fromSum: boolean;
}

const TERM_BREAK_BLOCKERS = "eE^(";

/** Split on "+" between terms, but not in 1e+3, 10^+3 or (+3). */
export function splitTerms(text: string): string[] {
  const terms: string[] = [];
  let current = "";
  let previous = "";
  for (const ch of text) {
    if (ch === "+" && !TERM_BREAK_BLOCKERS.includes(previous)) {
      terms.push(current);
      current = "";
    } else {
      current += ch;
    }
    if (ch !== " ") {
      previous = ch;
    }
  }
  terms.push(current);
  return terms;
}

/**
 * A number, or a sum of terms in expanded form (4000 + 500 + 30 + 2, or
 * 4 × 10^3 + 5 × 10^2), read as its exact standard-form value.
 */
export function parseExpandedInput(raw: string): ExpandedInput | null {
  const text = raw.trim();
  const body = text.startsWith("+") ? text.slice(1) : text;
  const terms = splitTerms(body);
  if (terms.length > 1) {
    let total: Rational = RATIONAL_ZERO;
    for (const term of terms) {
      const value = termToRational(term);
      if (value === null) {
        return null;
      }
      total = addR(total, value);
    }
    const decimal = rationalToDecimal(total);
    return decimal ? { value: decimal, fromSum: true } : null;
  }
  const decimal = parseDecimal(body);
  return decimal ? { value: decimal, fromSum: false } : null;
}
